import type { ApiError, ApiSuccess, AuthSessionDto } from '@talentbabu/types';
import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../app/auth-store';

export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

export const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 20_000,
  // Required by the API's CSRF guard on cookie-authenticated endpoints.
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
});

http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

const NO_REFRESH = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/register', '/auth/otp/verify'];

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(attempt = 0): Promise<boolean> {
  try {
    const res = await axios.post<ApiSuccess<AuthSessionDto>>(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true, headers: { 'X-Requested-With': 'XMLHttpRequest' } },
    );
    useAuthStore.getState().setSession(res.data.data);
    return true;
  } catch (err) {
    // Another tab rotated the token a moment ago; the browser now holds the new cookie.
    const code = (err as AxiosError<ApiError>).response?.data?.errors?.[0]?.code;
    if (code === 'TOKEN_ROTATED' && attempt === 0) {
      await new Promise((r) => setTimeout(r, 400));
      return doRefresh(1);
    }
    useAuthStore.getState().clear();
    return false;
  }
}

/** Single-flight refresh: concurrent 401s share one refresh request. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

http.interceptors.response.use(undefined, async (error: AxiosError<ApiError>) => {
  const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
  const url = config?.url ?? '';
  if (
    error.response?.status === 401 &&
    config &&
    !config._retried &&
    useAuthStore.getState().accessToken &&
    !NO_REFRESH.some((p) => url.startsWith(p))
  ) {
    config._retried = true;
    if (await refreshSession()) return http(config);
  }
  throw error;
});

/* Typed helpers returning the full envelope (data + meta). */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig) {
  return (await http.get<ApiSuccess<T>>(url, config)).data;
}
export async function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
  return (await http.post<ApiSuccess<T>>(url, body, config)).data;
}
export async function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
  return (await http.patch<ApiSuccess<T>>(url, body, config)).data;
}
export async function apiPut<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
  return (await http.put<ApiSuccess<T>>(url, body, config)).data;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig) {
  return (await http.delete<ApiSuccess<T>>(url, config)).data;
}
